import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { DialogService } from '@gains-com/ui-angular';
import { VipViewService } from '../../../../core/view-states/vip-view.service.js';
import { LevelUpRewardDialogComponent, LevelUpRewardDialogData } from './level-up-reward-dialog.component.js';
import {
  LevelUpReward,
  LevelUpRewardsListDialogComponent,
  LevelUpRewardsListDialogData,
} from './level-up-rewards-list-dialog.component.js';

/**
 * Opens the VIP level-up reward dialogs. Shared by the `/vip` reward pill and the
 * global level-up toast so both behave identically — and so the toast can claim a
 * reward *over the current screen* without navigating to `/vip` (an in-game
 * level-up shouldn't kick the player out of their game).
 *
 * Two entry points:
 *  - {@link openLatest} (toast) — the single-reward dialog for the tier the user
 *    just reached.
 *  - {@link open} (pill) — a list of all unclaimed rewards, or the single-reward
 *    dialog when there's only one.
 *
 * The claim id + reward amounts aren't carried on the realtime level-up event,
 * so they're resolved from {@link VipViewService} at open time (it has already
 * been refreshed when the level-up arrives). Lives here rather than on
 * `VipViewService` to avoid a cycle — the dialogs import `VipViewService`.
 */
@Injectable({ providedIn: 'root' })
export class LevelUpRewardDialogService {
  private readonly _dialog = inject(DialogService);
  private readonly _vipView = inject(VipViewService);

  /**
   * Toast / just-leveled-up entry point: opens the single-reward dialog for the
   * tier the user just reached (`vipLevelConfigId`), falling back to the newest
   * unclaimed reward. No-ops when there's nothing to claim.
   */
  async openLatest(vipLevelConfigId?: string): Promise<void> {
    const rewards = await this._resolveRewards();
    if (!rewards.length) {
      return;
    }

    const target =
      (vipLevelConfigId && rewards.find((r) => r.claim.vip_level_config_id === vipLevelConfigId)) ||
      rewards.reduce((newest, r) => (r.claim.created_at > newest.claim.created_at ? r : newest));

    this._openSingle(target);
  }

  /**
   * VIP-page pill entry point: opens a list of all unclaimed level-up rewards,
   * or the single-reward dialog when there's only one. No-ops when empty.
   */
  async open(): Promise<void> {
    const rewards = await this._resolveRewards();
    if (!rewards.length) {
      return;
    }

    if (rewards.length === 1) {
      this._openSingle(rewards[0]);
      return;
    }

    const ref = this._dialog.open<boolean, LevelUpRewardsListDialogData>(LevelUpRewardsListDialogComponent, {
      data: { rewards },
    });

    ref.closed.subscribe((claimed) => {
      if (claimed) {
        this._vipView.refresh();
      }
    });
  }

  /** Pairs each unclaimed claim (created_at asc, as the API returns) with its tier config. */
  private async _resolveRewards(): Promise<LevelUpReward[]> {
    const [claims, configs] = await Promise.all([
      firstValueFrom(this._vipView.unclaimedLevelRewards$),
      firstValueFrom(this._vipView.configs$),
    ]);

    return claims.map((claim) => ({
      claim,
      config: configs.find((c) => c.vip_level_config_id === claim.vip_level_config_id) ?? null,
    }));
  }

  private _openSingle(reward: LevelUpReward): void {
    const ref = this._dialog.open<boolean, LevelUpRewardDialogData>(LevelUpRewardDialogComponent, {
      data: { claim: reward.claim, config: reward.config },
    });

    ref.closed.subscribe((claimed) => {
      if (claimed) {
        this._vipView.refresh();
      }
    });
  }
}
